import * as React from "react";
import _ from "lodash";
import { Fragment, useState } from "react";
import { Link, graphql, navigate, useStaticQuery } from "gatsby";
import { Dialog, Popover, Transition } from "@headlessui/react";

import {
  MenuIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/react/outline";
import {
  PhoneIcon,
  UserIcon,
  HeartIcon,
  EyeIcon,
  LogoutIcon,
  StarIcon,
} from "@heroicons/react/solid";

import { IoCart } from "@react-icons/all-files/io5/IoCart";
import { Button, Text } from "@uikit";
import { slugify } from "@kalabo/golfsavers-common";
import { DisableScroll, EnableScroll, isRegionalSite } from "@utilities/core";
import CartSummary from "@components/checkout/cartSummary";
import LoginSignUp from "./loginSignup";
import useAuthUser from "@hooks/useAuthUser";
import { BREAKPOINTS } from "@utilities/constants";
import { useBreakpoint } from "use-breakpoint";
import { IoCloseOutline } from "@react-icons/all-files/io5/IoCloseOutline";
import { useApp } from "@store/app";
import { useAuth } from "@store/auth";
import { useCheckout } from "@store/checkout";
import { Badge } from "antd";
import { useDocument } from "@hooks/useDocument";

function classNames(...classes: any) {
  return classes.filter(Boolean).join(" ");
}

interface IProps {
  searchMode?: boolean;
  hideNav?: boolean;
}

const Navbar = React.memo(({ searchMode, hideNav = false }: IProps) => {
  const regionalMode = isRegionalSite();
  const doc = useDocument();

  const showCurrencyPicker = useApp(($) => $.showCurrencyPicker);
  const display_currency = useApp(($) => $.display_currency);
  const setShowCurrencyPicker = useApp(($) => $.setShowCurrencyPicker);
  const setShowFindBookingModal = useApp(($) => $.setShowFindBookingModal);
  const setShowLoginModal = useApp(($) => $.setShowLoginModal);
  const logout = useAuth(($) => $.logout);

  const userToken = useAuth(($) => $.userToken);
  const userInfo = useAuth(($) => $.userInfo);
  const userProfile = useAuth(($) => $.userProfile);

  const cart = useCheckout(($) => $.cart);

  const { breakpoint } = useBreakpoint(BREAKPOINTS, "mobile");
  const [showModal, setShowModal] = React.useState(false);

  const { user, profile } = useAuthUser();

  const [direction, setDirection] = useState("");
  const [activeMenu, setActiveMenu] = useState("");

  const buttonCartRef = React.useRef(null);
  const buttonContactRef = React.useRef(null);
  const buttonUserMenuRef = React.useRef(null);
  const timeoutDuration = 200;
  let timeout: any;

  const closeCartPopover = () => {
    return buttonCartRef.current?.dispatchEvent(
      new KeyboardEvent("keydown", {
        key: "Escape",
        bubbles: true,
        cancelable: true,
      })
    );
  };

  const closeContactPopover = () => {
    return buttonContactRef.current?.dispatchEvent(
      new KeyboardEvent("keydown", {
        key: "Escape",
        bubbles: true,
        cancelable: true,
      })
    );
  };

  const closeUserMenu = () => {
    return buttonUserMenuRef.current?.dispatchEvent(
      new KeyboardEvent("keydown", {
        key: "Escape",
        bubbles: true,
        cancelable: true,
      })
    );
  };

  const onMouseContactEnter = (open: boolean) => {
    clearTimeout(timeout);
    if (open) return;
    return buttonContactRef.current?.click();
  };

  const onMouseContactLeave = (open: boolean) => {
    if (!open) return;
    timeout = setTimeout(() => closeContactPopover(), timeoutDuration);
  };
  const onMouseCartEnter = (open: boolean) => {
    clearTimeout(timeout);
    if (open) return;
    return buttonCartRef.current?.click();
  };

  const onMouseCartLeave = (open: boolean) => {
    if (!open) return;
    timeout = setTimeout(() => closeCartPopover(), timeoutDuration);
  };

  const onUserMenuEnter = (open: boolean) => {
    clearTimeout(timeout);
    if (open) return;
    return buttonUserMenuRef.current?.click();
  };

  const onUserMenuLeave = (open: boolean) => {
    if (!open) return;
    timeout = setTimeout(() => closeUserMenu(), timeoutDuration);
  };

  const navigation = {
    footer: [
      {
        name: "Privacy",
        href: "/privacy-policy",
      },
      {
        name: "Terms",
        href: "/terms-and-conditions",
      },
    ],
    quicklinks: [
      { name: "About Us", href: "/about-us" },
      { name: "Our Reviews", href: "/reviews" },
      {
        name: "Manage Your Booking",
        onClick: () => {
          if (userToken) {
            navigate("/account/bookings");
          } else {
            setShowFindBookingModal();
          }
        },
      },
    ],
    social: [
      {
        name: "Facebook",
        href: "/",
        icon: (props: any) => (
          <svg fill="currentColor" viewBox="0 0 24 24" {...props}>
            <path
              d="M20.25 0H3.75C1.67893 0 0 1.67893 0 3.75V20.25C0 22.3211 1.67893 24 3.75 24H20.25C22.3211 24 24 22.3211 24 20.25V3.75C24 1.67893 22.3211 0 20.25 0Z"
              fill="#1877F2"
            />
            <path
              d="M18 12C18 8.7 15.3 6 12 6C8.7 6 6 8.7 6 12C6 15 8.175 17.475 11.025 17.925V13.725H9.525V12H11.025V10.65C11.025 9.15 11.925 8.325 13.275 8.325C13.95 8.325 14.625 8.475 14.625 8.475V9.975H13.875C13.125 9.975 12.9 10.425 12.9 10.875V12H14.55L14.25 13.725H12.825V18C15.825 17.55 18 15 18 12Z"
              fill="white"
            />
          </svg>
        ),
      },
      {
        name: "Twitter",
        href: "/",
        icon: (props: any) => (
          <svg fill="currentColor" viewBox="0 0 24 24" {...props}>
            <path
              d="M20.25 0H3.75C1.67893 0 0 1.67893 0 3.75V20.25C0 22.3211 1.67893 24 3.75 24H20.25C22.3211 24 24 22.3211 24 20.25V3.75C24 1.67893 22.3211 0 20.25 0Z"
              fill="#1DA1F2"
            />
            <path
              d="M18 8.25C17.55 8.475 17.1 8.55 16.575 8.625C17.1 8.325 17.475 7.875 17.625 7.275C17.175 7.575 16.65 7.725 16.05 7.875C15.6 7.425 14.925 7.125 14.25 7.125C12.675 7.125 11.475 8.625 11.85 10.125C9.825 10.05 8.025 9.075 6.75 7.575C6.075 8.7 6.45 10.125 7.5 10.875C7.125 10.875 6.75 10.725 6.375 10.575C6.375 11.7 7.2 12.75 8.325 13.05C7.95 13.125 7.575 13.2 7.2 13.125C7.5 14.1 8.4 14.85 9.525 14.85C8.625 15.525 7.275 15.9 6 15.75C7.125 16.425 8.4 16.875 9.75 16.875C14.325 16.875 16.875 13.05 16.725 9.525C17.25 9.225 17.7 8.775 18 8.25Z"
              fill="white"
            />
          </svg>
        ),
      },
      {
        name: "LinkedIn",
        href: "/",
        icon: (props: any) => (
          <svg fill="currentColor" viewBox="0 0 24 24" {...props}>
            <path
              d="M20.25 0H3.75C1.67893 0 0 1.67893 0 3.75V20.25C0 22.3211 1.67893 24 3.75 24H20.25C22.3211 24 24 22.3211 24 20.25V3.75C24 1.67893 22.3211 0 20.25 0Z"
              fill="#2867B2"
            />
            <path
              d="M8.7 18H6.15V9.975H8.7V18ZM7.425 8.85C6.6 8.85 6 8.25 6 7.425C6 6.6 6.675 6 7.425 6C8.25 6 8.85 6.6 8.85 7.425C8.85 8.25 8.25 8.85 7.425 8.85ZM18 18H15.45V13.65C15.45 12.375 14.925 12 14.175 12C13.425 12 12.675 12.6 12.675 13.725V18H10.125V9.975H12.525V11.1C12.75 10.575 13.65 9.75 14.925 9.75C16.35 9.75 17.85 10.575 17.85 13.05V18H18Z"
              fill="white"
            />
          </svg>
        ),
      },
      {
        name: "YouTube",
        href: "/",
        icon: (props: any) => (
          <svg fill="currentColor" viewBox="0 0 24 24" {...props}>
            <path
              d="M20.25 0H3.75C1.67893 0 0 1.67893 0 3.75V20.25C0 22.3211 1.67893 24 3.75 24H20.25C22.3211 24 24 22.3211 24 20.25V3.75C24 1.67893 22.3211 0 20.25 0Z"
              fill="#FF0000"
            />
            <path
              d="M17.7 9.07578C17.55 8.55078 17.175 8.17578 16.65 8.02578C15.75 7.80078 11.925 7.80078 11.925 7.80078C11.925 7.80078 8.175 7.80078 7.2 8.02578C6.675 8.17578 6.3 8.55078 6.15 9.07578C6 10.0508 6 12.0008 6 12.0008C6 12.0008 6 13.9508 6.225 14.9258C6.375 15.4508 6.75 15.8258 7.275 15.9758C8.175 16.2008 12 16.2008 12 16.2008C12 16.2008 15.75 16.2008 16.725 15.9758C17.25 15.8258 17.625 15.4508 17.775 14.9258C18 13.9508 18 12.0008 18 12.0008C18 12.0008 18 10.0508 17.7 9.07578ZM10.8 13.8008V10.2008L13.95 12.0008L10.8 13.8008Z"
              fill="white"
            />
          </svg>
        ),
      },
      {
        name: "Instagram",
        href: "/",
        icon: (props: any) => (
          <svg fill="currentColor" viewBox="0 0 24 24" {...props}>
            <path
              d="M20.25 0H3.75C1.67893 0 0 1.67893 0 3.75V20.25C0 22.3211 1.67893 24 3.75 24H20.25C22.3211 24 24 22.3211 24 20.25V3.75C24 1.67893 22.3211 0 20.25 0Z"
              fill="#F00073"
            />
            <path
              d="M11.9999 6.90039C13.6499 6.90039 13.8749 6.90039 14.5499 6.90039C15.1499 6.90039 15.4499 7.05039 15.6749 7.12539C15.9749 7.27539 16.1999 7.35039 16.4249 7.57539C16.6499 7.80039 16.7999 8.02539 16.8749 8.32539C16.9499 8.55039 17.0249 8.85039 17.0999 9.45039C17.0999 10.1254 17.0999 10.2754 17.0999 12.0004C17.0999 13.7254 17.0999 13.8754 17.0999 14.5504C17.0999 15.1504 16.9499 15.4504 16.8749 15.6754C16.7249 15.9754 16.6499 16.2004 16.4249 16.4254C16.1999 16.6504 15.9749 16.8004 15.6749 16.8754C15.4499 16.9504 15.1499 17.0254 14.5499 17.1004C13.8749 17.1004 13.7249 17.1004 11.9999 17.1004C10.2749 17.1004 10.1249 17.1004 9.4499 17.1004C8.8499 17.1004 8.5499 16.9504 8.3249 16.8754C8.0249 16.7254 7.7999 16.6504 7.5749 16.4254C7.3499 16.2004 7.1999 15.9754 7.1249 15.6754C7.0499 15.4504 6.9749 15.1504 6.8999 14.5504C6.8999 13.8754 6.8999 13.7254 6.8999 12.0004C6.8999 10.2754 6.8999 10.1254 6.8999 9.45039C6.8999 8.85039 7.0499 8.55039 7.1249 8.32539C7.2749 8.02539 7.3499 7.80039 7.5749 7.57539C7.7999 7.35039 8.0249 7.20039 8.3249 7.12539C8.5499 7.05039 8.8499 6.97539 9.4499 6.90039C10.1249 6.90039 10.3499 6.90039 11.9999 6.90039ZM11.9999 5.77539C10.2749 5.77539 10.1249 5.77539 9.4499 5.77539C8.7749 5.77539 8.3249 5.92539 7.9499 6.07539C7.5749 6.22539 7.1999 6.45039 6.8249 6.82539C6.4499 7.20039 6.2999 7.50039 6.0749 7.95039C5.9249 8.32539 5.8499 8.77539 5.7749 9.45039C5.7749 10.1254 5.7749 10.3504 5.7749 12.0004C5.7749 13.7254 5.7749 13.8754 5.7749 14.5504C5.7749 15.2254 5.9249 15.6754 6.0749 16.0504C6.2249 16.4254 6.4499 16.8004 6.8249 17.1754C7.1999 17.5504 7.4999 17.7004 7.9499 17.9254C8.3249 18.0754 8.7749 18.1504 9.4499 18.2254C10.1249 18.2254 10.3499 18.2254 11.9999 18.2254C13.6499 18.2254 13.8749 18.2254 14.5499 18.2254C15.2249 18.2254 15.6749 18.0754 16.0499 17.9254C16.4249 17.7754 16.7999 17.5504 17.1749 17.1754C17.5499 16.8004 17.6999 16.5004 17.9249 16.0504C18.0749 15.6754 18.1499 15.2254 18.2249 14.5504C18.2249 13.8754 18.2249 13.6504 18.2249 12.0004C18.2249 10.3504 18.2249 10.1254 18.2249 9.45039C18.2249 8.77539 18.0749 8.32539 17.9249 7.95039C17.7749 7.57539 17.5499 7.20039 17.1749 6.82539C16.7999 6.45039 16.4999 6.30039 16.0499 6.07539C15.6749 5.92539 15.2249 5.85039 14.5499 5.77539C13.8749 5.77539 13.7249 5.77539 11.9999 5.77539Z"
              fill="white"
            />
            <path
              d="M11.9999 8.77539C10.1999 8.77539 8.7749 10.2004 8.7749 12.0004C8.7749 13.8004 10.1999 15.2254 11.9999 15.2254C13.7999 15.2254 15.2249 13.8004 15.2249 12.0004C15.2249 10.2004 13.7999 8.77539 11.9999 8.77539ZM11.9999 14.1004C10.8749 14.1004 9.8999 13.2004 9.8999 12.0004C9.8999 10.8754 10.7999 9.90039 11.9999 9.90039C13.1249 9.90039 14.0999 10.8004 14.0999 12.0004C14.0999 13.1254 13.1249 14.1004 11.9999 14.1004Z"
              fill="white"
            />
            <path
              d="M15.2999 9.45039C15.7141 9.45039 16.0499 9.1146 16.0499 8.70039C16.0499 8.28618 15.7141 7.95039 15.2999 7.95039C14.8857 7.95039 14.5499 8.28618 14.5499 8.70039C14.5499 9.1146 14.8857 9.45039 15.2999 9.45039Z"
              fill="white"
            />
          </svg>
        ),
      },
      {
        name: "Pinterest",
        href: "/",
        icon: (props: any) => (
          <svg fill="currentColor" viewBox="0 0 24 24" {...props}>
            <path
              d="M20.25 0H3.75C1.67893 0 0 1.67893 0 3.75V20.25C0 22.3211 1.67893 24 3.75 24H20.25C22.3211 24 24 22.3211 24 20.25V3.75C24 1.67893 22.3211 0 20.25 0Z"
              fill="#E60023"
            />
            <path
              d="M12 6C8.7 6 6 8.7 6 12C6 14.475 7.5 16.575 9.6 17.475C9.6 17.025 9.6 16.575 9.675 16.125C9.825 15.6 10.425 12.825 10.425 12.825C10.425 12.825 10.2 12.45 10.2 11.85C10.2 10.95 10.725 10.275 11.325 10.275C11.85 10.275 12.15 10.65 12.15 11.175C12.15 11.7 11.775 12.525 11.625 13.275C11.475 13.875 11.925 14.4 12.6 14.4C13.725 14.4 14.475 12.975 14.475 11.175C14.475 9.825 13.575 8.85 12 8.85C10.2 8.85 9.075 10.2 9.075 11.7C9.075 12.225 9.225 12.6 9.45 12.9C9.525 13.05 9.6 13.05 9.525 13.2C9.525 13.275 9.45 13.575 9.375 13.65C9.3 13.8 9.225 13.875 9.075 13.8C8.25 13.425 7.875 12.525 7.875 11.475C7.875 9.75 9.3 7.725 12.15 7.725C14.475 7.725 15.975 9.375 15.975 11.175C15.975 13.5 14.7 15.3 12.75 15.3C12.075 15.3 11.475 14.925 11.25 14.55C11.25 14.55 10.875 15.9 10.8 16.2C10.65 16.65 10.425 17.1 10.2 17.475C10.725 17.625 11.325 17.7 11.925 17.7C15.225 17.7 17.925 15 17.925 11.7C18 8.7 15.3 6 12 6Z"
              fill="white"
            />
          </svg>
        ),
      },
    ],
  };

  let quicklinks = React.useMemo(() => {
    let links = _.cloneDeep(navigation.quicklinks);
    if (regionalMode) {
      links.unshift({
        name: `${process.env.GATSBY_REGION} Golf Packages`,
        href: `/${slugify(
          process.env.GATSBY_REGION.toLowerCase()
        )}-golf-packages`,
      });
      links.unshift({
        name: `${process.env.GATSBY_REGION} Golf Courses`,
        href: `/${slugify(
          process.env.GATSBY_REGION.toLowerCase()
        )}-golf-courses`,
      });
    }

    return links;
  }, [navigation]);

  React.useEffect(() => {}, []);

  let oldy = 0;
  let dir = "";
  const handleMouseMove = (event: { pageY: number }) => {
    if (event.pageY < oldy) {
      dir = "up";
    } else if (event.pageY > oldy) {
      dir = "down";
    }
    oldy = event.pageY;

    setDirection(dir);
  };

  const userNavigation = React.useMemo(() => {
    return [
      {
        name: "My Account",
        onClick: () => navigate("/account"),
        icon: UserIcon,
        count: 0,
      },
      {
        name: "Active Bookings",
        onClick: () => navigate("/account/bookings"),
        icon: StarIcon,
        count: userProfile?.orders?.length || 0,
      },
      {
        name: "My Favourites",
        onClick: () => navigate("/account/favourites"),
        icon: HeartIcon,
        count: 0,
      },
      {
        name: "Recently Viewed",
        onClick: () => navigate("/account/recentlyviewed"),
        icon: EyeIcon,
        count: 0,
      },
      { name: "Log out", onClick: () => logout(), icon: LogoutIcon, count: 0 },
    ];
  }, [userProfile, logout]);

  const data = useStaticQuery(graphql`
    query {
      agents: allGolfsaversAgents {
        edges {
          node {
            config {
              email {
                contact_email
                contact_phone
                reply_address
                from_alias
              }
            }
          }
        }
      }
      regions: allGolfsaversCountryRegions {
        edges {
          node {
            region_id
            name {
              en
            }
            slug
          }
        }
      }
      countries: allGolfsaversCountries {
        edges {
          node {
            topnav_config {
              enabled
              description {
                en
              }
              order
            }
            slug
            name {
              en
            }
            id
            active
            regions {
              id
              name {
                en
              }
              slug
              courses {
                id
                images
              }
            }
          }
        }
      }
    }
  `);

  const agent = data.agents.edges[0].node;

  const region = regionalMode
    ? data.regions.edges.find(({ node }: any) => {
        return node.region_id === process.env.GATSBY_REGION_ID.toLowerCase();
      })
    : null;

  const getNavigationData = React.useCallback(() => {
    const navData: any = {
      categories: [],
      allDestinations: [],
    };

    data.countries.edges.forEach(({ node }: any) => {
      let courseCount = node.regions.reduce((acc: any, region: any) => {
        return acc + region.courses.length;
      }, 0);

      if (node.topnav_config && node.topnav_config.enabled) {
        navData.categories.push({
          id: node.slug,
          name: node.name.en,
          slug: node.slug,
          description: node.topnav_config.description.en,
          order: node.topnav_config.order,
          regions: node.regions,
          courses: courseCount,
        });
      }

      navData.allDestinations.push({
        id: node.slug,
        name: node.name.en,
        slug: node.slug,
        courses: courseCount,
      });
    });

    navData.categories.sort((a: { name: string }, b: { name: any }) => {
      return a.name.localeCompare(b.name);
    });
    navData.allDestinations.sort((a: { name: string }, b: { name: any }) => {
      return a.name.localeCompare(b.name);
    });

    return navData;
  }, [data]);

  let importOrder = ["thailand", "indonesia", "malaysia", "vietnam"];

  const sortByObject = importOrder.reduce((obj, item, index) => {
    return {
      ...obj,
      [item]: index,
    };
  }, {});

  const sortByOrder = (order: string[]) => {
    return order.reduce((obj, item, index) => {
      return {
        ...obj,
        [item]: index,
      };
    }, {});
  };

  const closestEdge = (mouse: any) => {
    let elem = mouse.target;
    var elemBounding = elem.getBoundingClientRect();

    var elementLeftEdge = elemBounding.left;
    var elementTopEdge = elemBounding.top;
    var elementRightEdge = elemBounding.right;
    var elementBottomEdge = elemBounding.bottom;

    var mouseX = mouse.pageX;
    var mouseY = mouse.pageY;

    var topEdgeDist = Math.abs(elementTopEdge - mouseY);
    var bottomEdgeDist = Math.abs(elementBottomEdge - mouseY);
    var leftEdgeDist = Math.abs(elementLeftEdge - mouseX);
    var rightEdgeDist = Math.abs(elementRightEdge - mouseX);

    var min = Math.min(
      topEdgeDist,
      bottomEdgeDist,
      leftEdgeDist,
      rightEdgeDist
    );

    switch (min) {
      case leftEdgeDist:
        return "left";
      case rightEdgeDist:
        return "right";
      case topEdgeDist:
        return "top";
      case bottomEdgeDist:
        return "bottom";
    }
  };

  const [open, setOpen] = useState(false);
  const [submenuOpen, setSubmenuOpen] = useState(false);

  const [submenuSection, setSubmenuSection] = useState(null);

  const mobileLoginButton = React.useMemo(() => {
    if (!userToken) {
      return (
        <Button
          text={userToken ? "Sign out" : "Sign up or Log in"}
          color={"blue"}
          invert
          size="medium"
          className=" !mb-0  !border-grey3 w-full "
          onClick={() => {
            setOpen(false);
            if (!userToken) {
              setShowLoginModal();
            }
          }}
        />
      );
    }

    return (
      <div>
        <div className="flex gap-3 items-center">
          {user?.user_metadata?.picture ? (
            <img
              alt={`${profile?.first_name} ${profile?.last_name}`}
              src={user?.user_metadata?.picture}
              className=" w-6 h-6 rounded-full overflow-hidden"
            />
          ) : (
            <img
              alt={`${profile?.first_name} ${profile?.last_name}`}
              src={`https://ui-avatars.com/api/?name=${profile?.first_name}+${profile?.last_name}`}
              className=" w-6 h-6 rounded-full overflow-hidden"
            />
          )}
          <Text size={17} leading={1.3} weight="semibold" color="black90">
            {profile?.first_name} {profile?.last_name}
          </Text>
        </div>
        <div className="mt-2 mb-3">
          {userNavigation.map((item, idx) => (
            <div
              key={idx}
              onClick={item.onClick}
              className={
                "flex cursor-pointer items-center mb-1 py-2 text-sm text-13 font-semibold"
              }
            >
              {React.createElement(item.icon, {
                className: "w-4 h-4 mr-2 text-grey5",
              })}

              <Badge count={item.count} offset={[10, 0]} size="small">
                {item.name}
              </Badge>
            </div>
          ))}
        </div>
      </div>
    );
  }, [userToken, user, profile, userNavigation, showCurrencyPicker]);

  const loginButton = React.useMemo(() => {
    if (!userToken) {
      return (
        <Button
          text={userToken ? "Sign out" : "Sign up or Log in"}
          color={"blue"}
          invert
          size="medium"
          className="hidden !mb-0 lg:flex !px-4 hover:!bg-blue hover:!text-white"
          onClick={() => {
            if (!userToken) {
              setShowLoginModal();
            }
          }}
        />
      );
    }

    const picture = userInfo?.user_metadata?.picture;

    if (["mobile", "tablet", "large_tablet"].includes(breakpoint)) {
      return (
        <a
          onClick={() => setOpen(true)}
          className={`relative border border-grey3 sm:border-none  rounded-[4px] p-1 text-center flex justify-center items-center h-8 w-8 sm:h-10 sm:w-10 `}
        >
          <div className="rounded-full text-13 bg-grey4 text-white flex items-center justify-center uppercase overflow-hidden">
            {picture ? (
              <img src={picture} />
            ) : (
              <>
                {!profile ? (
                  <></>
                ) : (
                  <img
                    alt={`${profile?.first_name} ${profile?.last_name}`}
                    src={`https://ui-avatars.com/api/?name=${profile?.first_name}+${profile?.last_name}`}
                  />
                )}
              </>
            )}
          </div>
        </a>
      );
    }

    return (
      <div
        onMouseLeave={() => {
          onUserMenuLeave(open);
        }}
      >
        <Popover className="md:relative">
          {({ open, close }) => (
            <>
              <Popover.Button
                className={`focus:outline-none focus:ring-0 rounded-[4px] flex items-center justify-center gap-2 bg-white border border-grey3 p-2 h-[42px]   ${
                  open
                    ? "!bg-blue !text-white"
                    : "hover:!bg-blue hover:!text-white"
                }`}
                ref={buttonUserMenuRef}
                onMouseEnter={() => {
                  onUserMenuEnter(open);
                }}
                onMouseLeave={() => {
                  onUserMenuLeave(open);
                }}
              >
                <span className="sr-only">Open user menu</span>
                <MenuIcon className="w-6 h-6 " aria-hidden="true" />
                <div className="w-7 h-7 rounded-full text-13 bg-grey4 text-white flex items-center justify-center uppercase overflow-hidden">
                  {picture ? (
                    <img src={picture} />
                  ) : (
                    <>
                      {!profile ? (
                        <></>
                      ) : (
                        <img
                          alt={`${profile?.first_name} ${profile?.last_name}`}
                          src={`https://ui-avatars.com/api/?name=${profile?.first_name}+${profile?.last_name}`}
                        />
                      )}
                    </>
                  )}
                </div>
              </Popover.Button>

              <Transition
                as={Fragment}
                enter="transition ease-out duration-200"
                enterFrom="opacity-0 translate-y-1"
                enterTo="opacity-100 translate-y-0 sm:translate-y-2"
                leave="transition ease-in duration-150"
                leaveFrom="opacity-100 translate-y-0"
                leaveTo="opacity-0 translate-y-1"
              >
                <Popover.Panel
                  className="fixed z-[1000] top-0 sm:top-auto bg-black/60 sm:bg-transparent items-end flex bottom-0 sm:bottom-auto left-0 right-0 sm:absolute sm:left-auto"
                  onClick={(e: any) => {
                    // if clicked directly on the panel, close the popover
                    if (e.target === e.currentTarget) {
                      close();
                    }
                  }}
                >
                  <div
                    onMouseEnter={() => {
                      onUserMenuEnter(open);
                    }}
                    onMouseLeave={() => {
                      onUserMenuLeave(open);
                    }}
                    className="overflow-hidden md:rounded-lg  ring-opacity-5 rounded-t-md w-full shadow-2xl "
                  >
                    <div className="shadow-modal border-[1px] border-grey3 overflow-hidden rounded-lg">
                      <div className="border border-grey3 pt-3 absolute right-0 z-10  w-48 origin-top-right rounded-md bg-white  shadow-lg  ring-opacity-5 focus:outline-none">
                        {userNavigation.map((item, index) => {
                          // is last item?
                          let last_item = index === userNavigation.length - 1;
                          return (
                            <div
                              key={index}
                              onClick={item.onClick}
                              className={`${
                                last_item
                                  ? "border-t mt-3 border-grey3 py-3 "
                                  : "  py-2 "
                              } flex items-center hover:bg-grey2 cursor-pointer px-4 text-sm text-black/90 text-13 font-semibold`}
                            >
                              {React.createElement(item.icon, {
                                className: "w-5 h-5 mr-2 text-grey5",
                              })}
                              <Badge
                                count={item.count}
                                offset={[10, 0]}
                                size="small"
                              >
                                {item.name}
                              </Badge>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </Popover.Panel>
              </Transition>
            </>
          )}
        </Popover>
      </div>
    );
  }, [userToken, profile, breakpoint, setShowLoginModal]);

  const proceedToChekout = React.useCallback(() => {
    if (userToken) {
      navigate("/checkout");
    } else {
      setShowModal(true);
    }
    return false;
  }, [userToken]);

  const contactButton = React.useMemo(() => {
    return (
      <div
        onMouseLeave={() => {
          if (breakpoint !== "mobile") onMouseContactLeave(open);
        }}
      >
        <Popover className="md:relative">
          {({ open, close }) => (
            <>
              <Popover.Button
                ref={buttonContactRef}
                className={"focus:outline-none focus:ring-0"}
                onClick={() => {
                  if (breakpoint === "mobile") {
                    DisableScroll(doc);
                  }
                }}
              >
                <Button
                  renderAs="div"
                  text="Contact Us"
                  color={open ? "blue" : "black"}
                  icon="phone"
                  invert={!open}
                  size="medium"
                  onClick={() => {}}
                  className="hidden !mb-0 xl:flex !border-grey3 !px-4 hover:!bg-blue hover:!text-white hover:!border-blue"
                />

                <div
                  className={`xl:hidden relative border border-grey3 sm:border-none  rounded-[4px] p-1 text-center flex justify-center items-center h-8 w-8 sm:h-10 sm:w-10 hover:bg-blue hover:text-white ${
                    open && "bg-blue"
                  }`}
                >
                  <span className="sr-only">Contact Us</span>
                  <PhoneIcon
                    className={`w-5 h-5 sm:w-7 sm:h-7 ${
                      open ? "text-white" : "hover:text-white"
                    }`}
                    aria-hidden="true"
                  />
                </div>
              </Popover.Button>

              <Transition
                as={Fragment}
                enter="transition ease-out duration-200"
                enterFrom="opacity-0 translate-y-1"
                enterTo="opacity-100 translate-y-0 sm:translate-y-2"
                leave="transition ease-in duration-150"
                leaveFrom="opacity-100 translate-y-0"
                leaveTo="opacity-0 translate-y-1"
              >
                <Popover.Panel
                  className="fixed z-[1000] top-0 sm:top-auto bg-black/60 sm:bg-transparent items-end flex bottom-0 sm:bottom-auto left-0 right-0 sm:absolute sm:left-auto"
                  onClick={(e: any) => {
                    // if clicked directly on the panel, close the popover
                    if (e.target === e.currentTarget) {
                      close();
                      EnableScroll(doc);
                    }
                  }}
                >
                  <div className="overflow-hidden md:rounded-lg shadow-lg ">
                    <div className="shadow-modal border-[1px] border-grey3 flex flex-col  w-full md:w-[415px] bg-white rounded-t-lg sm:rounded-lg  ">
                      <div className="sm:hidden border-b border-grey3 px-5 py-3 sm:py-8 sm:px-10 flex justify-end">
                        <button
                          type="button"
                          className="rounded-md inline-flex items-center justify-center text-gray-400"
                          onClick={(e) => {
                            close();
                            EnableScroll(doc);
                          }}
                        >
                          <span className="sr-only">Close menu</span>

                          <IoCloseOutline
                            className="h-6 w-6 text-grey7"
                            aria-hidden="true"
                          />
                        </button>
                      </div>
                      <div className="flex flex-col p-5 pb-16 sm:py-8 sm:px-10 sm:pb-8">
                        <Text
                          size={21}
                          leading={1.3}
                          weight="semibold"
                          className="mb-2"
                        >
                          Contact Us
                        </Text>
                        <Text
                          size={15}
                          leading={1.5}
                          color="black90"
                          className="mb-8"
                        >
                          Our customer service operators are available from{" "}
                          <span className="font-semibold">
                            8am to 6pm on weekdays
                          </span>{" "}
                          and{" "}
                          <span className="font-semibold">
                            8am to 5pm on weekends
                          </span>{" "}
                          (UTC+7).
                        </Text>
                        <div className="flex flex-col gap-5">
                          {agent?.config?.email?.contact_phone && (
                            <a
                              href={`tel:${agent.config.email.contact_phone}`}
                              className="flex gap-2 items-center"
                            >
                              <img
                                src={`${process.env.GATSBY_IMAGE_PREFIX}/website/icons/phone.png`}
                                alt="Phone"
                                className="h-9 w-auto"
                              />
                              <Text
                                size={15}
                                color="black"
                                weight="semibold"
                                className="underline"
                              >
                                {agent?.config?.email?.contact_phone}
                              </Text>
                            </a>
                          )}
                          {agent?.config?.email?.contact_email && (
                            <a
                              href={`mailto:${agent?.config?.email?.contact_email}`}
                              className="flex gap-2 items-center"
                            >
                              <img
                                src={`${process.env.GATSBY_IMAGE_PREFIX}/website/icons/email.png`}
                                alt="email"
                                className="h-9 w-auto"
                              />
                              <Text
                                size={15}
                                color="black"
                                weight="semibold"
                                className="underline"
                              >
                                {agent?.config?.email?.contact_email}
                              </Text>
                            </a>
                          )}
                          {/* <div
                            className="flex gap-2 items-center"
                            onClick={() => {
                              //Tawk_API.toggle();
                            }}
                          >
                            <img
                              src={`${process.env.GATSBY_IMAGE_PREFIX}/website/icons/chat.png`}
                              alt="chat"
                              className="h-9 w-auto"
                            />
                            <Text
                              size={15}
                              color="black"
                              weight="semibold"
                              className="underline"
                            >
                              Launch Chat
                            </Text>
                          </div> */}
                          <a
                            href="https://wa.me/66629715436"
                            target="_blank"
                            className="flex gap-2 items-center"
                          >
                            <img
                              src={`${process.env.GATSBY_IMAGE_PREFIX}/website/icons/whatsapp.png`}
                              alt="whatsapp"
                              className="h-9 w-auto"
                            />
                            <Text
                              size={15}
                              color="black"
                              weight="semibold"
                              className="underline"
                            >
                              Start Whatsapp Chat
                            </Text>
                          </a>
                          {/* <div className="flex gap-2 items-center">
                            <img
                              src={`${process.env.GATSBY_IMAGE_PREFIX}/website/icons/line.png`}
                              alt="line"
                              className="h-9 w-auto"
                            />
                            <Text
                              size={15}
                              color="black"
                              weight="semibold"
                              className="underline"
                            >
                              Start Line Chat
                            </Text>
                          </div> */}
                        </div>
                      </div>
                    </div>
                  </div>
                </Popover.Panel>
              </Transition>
            </>
          )}
        </Popover>
      </div>
    );
  }, [breakpoint, doc]);

  const checkoutButton = React.useMemo(() => {
    return (
      <div
        onMouseLeave={() => {
          if (breakpoint !== "mobile") onMouseCartLeave(open);
        }}
      >
        <Popover className="md:relative">
          {({ open, close }) => (
            <>
              <Popover.Button
                className={`focus:outline-none focus:ring-0 relative border border-grey3 sm:border-none  rounded-[4px] p-1 text-center flex justify-center items-center h-8 w-8 sm:h-10 sm:w-10 hover:bg-blue [&>span>svg]:hover:text-white  hover:!text-white ${
                  open && "bg-blue"
                } `}
                onClick={() => {
                  if (breakpoint === "mobile") {
                    DisableScroll(doc);
                  }
                }}
                ref={buttonCartRef}
              >
                <Badge
                  count={cart?.length}
                  className="font-normal"
                  size={"small"}
                  offset={breakpoint === "mobile" && [5, -5]}
                >
                  <span className="sr-only">Shopping Cart</span>
                  <IoCart
                    className={`w-5 h-5 sm:w-7 sm:h-7 ${
                      open ? "text-white" : "hover:!text-white"
                    }`}
                    aria-hidden="true"
                  />
                </Badge>
              </Popover.Button>

              <Transition
                as={Fragment}
                enter="transition ease-out duration-200"
                enterFrom="opacity-0 translate-y-1"
                enterTo="opacity-100 translate-y-0 sm:translate-y-2"
                leave="transition ease-in duration-150"
                leaveFrom="opacity-100 translate-y-0"
                leaveTo="opacity-0 translate-y-1"
              >
                <Popover.Panel
                  className="fixed z-[1000] top-0 sm:top-auto bg-black/60  sm:bg-transparent items-end flex bottom-0 sm:bottom-auto left-0 right-0 sm:absolute sm:left-auto"
                  onClick={(e: any) => {
                    // if clicked directly on the panel, close the popover
                    if (e.target === e.currentTarget) {
                      close();
                      EnableScroll(doc);
                    }
                  }}
                >
                  <div className="overflow-hidden md:rounded-lg  ring-opacity-5 rounded-t-md w-full shadow-2xl ">
                    <CartSummary
                      onClose={() => {
                        EnableScroll(doc);
                        close();
                      }}
                      onCheckout={() => {
                        proceedToChekout();
                        EnableScroll(doc);
                      }}
                    />
                  </div>
                </Popover.Panel>
              </Transition>
            </>
          )}
        </Popover>

        <LoginSignUp
          visible={showModal}
          defaultMode="checkout"
          onSignIn={() => {
            setShowModal(false);
            navigate("/checkout");
          }}
          onClose={() => {
            setShowModal(false);
          }}
        />
      </div>
    );
  }, [showModal, cart, breakpoint, doc]);

  return (
    <>
      <header
        className="relative bg-white border-b border-grey3 "
        onMouseMove={handleMouseMove}
      >
        {/* Mobile menu */}
        <Transition.Root show={open} as={Fragment}>
          <Dialog
            as="div"
            className="relative z-[999]  lg:hidden"
            onClose={() => {
              setOpen(false);
              setSubmenuOpen(false);
            }}
          >
            <Transition.Child as={Fragment}>
              <div className="fixed inset-0 bg-black bg-opacity-80">
                <div className="absolute right-5 md:right-8 top-6 md:top-12">
                  <div
                    className="-m-2 p-2 rounded-md inline-flex items-center justify-center text-gray-400"
                    onClick={() => setOpen(false)}
                  >
                    <span className="sr-only">Close menu</span>
                    <IoCloseOutline
                      className="h-8 w-8 text-white"
                      aria-hidden="true"
                    />
                  </div>
                </div>
              </div>
            </Transition.Child>

            <div className="fixed inset-0 flex z-40">
              <Transition.Child
                as={Fragment}
                enter="transition ease-in-out duration-300 transform"
                enterFrom="-translate-x-full"
                enterTo="translate-x-0"
                leave="transition ease-in-out duration-300 transform"
                leaveFrom="translate-x-0"
                leaveTo="-translate-x-full"
              >
                <Dialog.Panel className="relative  w-80 bg-white shadow-xl pb-12 flex flex-col overflow-y-auto">
                  <div className="mx-3 px-0 sm:px-2 md:px-6  pt-8 border-b-[1px] border-bordergrey2">
                    {/* Navbar Logo */}
                    <div className="flex-shrink-0 mb-5 sm:w-auto sm:mr-4 flex items-center">
                      <Link to="/" aria-label="Go to homepage">
                        <img
                          alt={process.env.GATSBY_SITE_URL}
                          src={process.env.GATSBY_LOGO}
                          className="h-[31px] sm:h-[38px]"
                        />
                      </Link>
                    </div>
                    {regionalMode ? (
                      <></>
                    ) : (
                      <>
                        <Text size={13} color="black" className="mb-3">
                          Destinations
                        </Text>
                        {getNavigationData()
                          .categories.sort(
                            (a: any, b: any) =>
                              sortByObject[a.slug] - sortByObject[b.slug]
                          )
                          .map((category: any, catIndex: any) => (
                            <div
                              key={catIndex}
                              className="flex flex-row mb-5 items-center cursor-pointer"
                              onClick={() => {
                                setSubmenuSection(category);
                                setSubmenuOpen(true);
                              }}
                            >
                              <img
                                src={`https://images.unsplash.com/photo-1538648759472-7251f7cb2c2f?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1548&q=80`}
                                alt={
                                  "https://images.unsplash.com/photo-1538648759472-7251f7cb2c2f?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1548&q=80"
                                }
                                className="h-[60px] w-[60px] mr-3 bg-black/50 rounded  object-center object-cover"
                              />

                              <div className="flex flex-1 flex-col">
                                <Text size={13} color="black80">
                                  {category.name}
                                </Text>
                                <Text size="small" color="black60">
                                  {category.courses} golf courses
                                </Text>
                              </div>

                              <ChevronRightIcon
                                className="text-blue h-6 w-6"
                                aria-hidden="true"
                              />
                            </div>
                          ))}
                        <div
                          className="flex flex-row mb-7 items-center"
                          onClick={() => {
                            setSubmenuSection("all");
                            setSubmenuOpen(true);
                          }}
                        >
                          <div className="flex flex-1 flex-col">
                            <Text size={13} color="black80">
                              All Destinations (A-Z)
                            </Text>
                          </div>

                          <ChevronRightIcon
                            className="text-blue h-6 w-6"
                            aria-hidden="true"
                          />
                        </div>
                      </>
                    )}
                  </div>{" "}
                  <div className="mx-3 px-0 sm:px-2 md:px-6  pt-8 pb-7 border-b-[1px] border-bordergrey2">
                    <Text
                      size={13}
                      color="black60"
                      weight="semibold"
                      className="mb-4"
                    >
                      My Account
                    </Text>

                    {mobileLoginButton}
                  </div>
                  <div className="mx-3 px-0 sm:px-2 md:px-6  pt-8 pb-7 border-b-[1px] border-bordergrey2">
                    <Text
                      size={13}
                      color="black60"
                      weight="semibold"
                      className="mb-4"
                    >
                      Menu
                    </Text>
                    <div className="flex flex-col gap-4">
                      {quicklinks.map((item, index) => {
                        if (item.onClick) {
                          return (
                            <div
                              className="cursor-pointer"
                              onClick={item.onClick}
                              key={index}
                            >
                              <Text
                                size={17}
                                leading={1.3}
                                weight="semibold"
                                color="black90"
                              >
                                {item.name}
                              </Text>
                            </div>
                          );
                        } else {
                          return (
                            <Link to={item.href} key={index}>
                              <Text
                                size={17}
                                leading={1.3}
                                weight="semibold"
                                color="black90"
                              >
                                {item.name}
                              </Text>
                            </Link>
                          );
                        }
                      })}
                    </div>
                  </div>
                  <div className="mx-3  px-0 sm:px-2 md:px-6 pt-8 pb-7 border-b-[1px] border-bordergrey2">
                    <Text
                      size={13}
                      color="black60"
                      weight="semibold"
                      className="mb-4"
                    >
                      Settings
                    </Text>
                    <div className="flex flex-col gap-4">
                      <div
                        className="flex flex-1 justify-between"
                        onClick={() => {
                          setOpen(false);
                          setSubmenuOpen(false);
                          setShowCurrencyPicker();
                        }}
                      >
                        <Text
                          size={15}
                          leading={1.3}
                          weight="semibold"
                          color="black90"
                        >
                          Price display
                        </Text>
                        <Text
                          size={15}
                          leading={1.3}
                          weight="semibold"
                          color="black90"
                        >
                          {display_currency.code}
                        </Text>
                      </div>
                    </div>
                  </div>
                  <div className="mx-3  px-0 sm:px-2 md:px-6  py-8">
                    <Text
                      size={13}
                      color="black60"
                      weight="semibold"
                      className="mb-3"
                    >
                      Follow Us
                    </Text>

                    <div className="flex space-x-2">
                      {navigation.social.map((item, itemIdx) => (
                        <Link to={item.href} key={itemIdx}>
                          <span className="sr-only">{item.name}</span>
                          <item.icon className="h-7 w-7" aria-hidden="true" />
                        </Link>
                      ))}
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>

            <Transition.Root show={submenuOpen} as={Fragment}>
              <Dialog
                as="div"
                className="relative z-40 lg:hidden"
                onClose={() => {
                  setOpen(false);
                  setSubmenuOpen(false);
                }}
              >
                <div className="fixed inset-0 flex z-50">
                  <Transition.Child
                    as={Fragment}
                    enter="transition ease-in-out duration-300 transform"
                    enterFrom="-translate-x-full"
                    enterTo="translate-x-0"
                    leave="transition ease-in-out duration-300 transform"
                    leaveFrom="translate-x-0"
                    leaveTo="-translate-x-full"
                  >
                    <Dialog.Panel className="relative w-5/6 bg-white shadow-xl pb-12 flex flex-col overflow-y-auto">
                      <div className="px-4 pt-5 pb-2 flexflex-col items-start">
                        <div
                          className="-m-2 p-2 rounded-md inline-flex items-center justify-center text-gray-400"
                          onClick={() => setSubmenuOpen(false)}
                        >
                          <span className="sr-only">Close menu</span>
                          <ChevronLeftIcon
                            className="h-6 w-6"
                            aria-hidden="true"
                          />
                        </div>

                        {submenuSection === "all" ? (
                          <>
                            <div className="flex flex-row items-center justify-between mb-2 mt-2">
                              <Text size="h2" className="mb-8">
                                All Destinations
                              </Text>
                            </div>
                            <div className="grid grid-cols-2 gap-6">
                              {getNavigationData().allDestinations.map(
                                (category: any, index: any) => (
                                  <Link to={`/${category.slug}`} key={index}>
                                    <Text size={13} color="black80">
                                      {category.name}
                                    </Text>
                                    <Text size="small" color="black60">
                                      {category.courses} golf courses
                                    </Text>
                                  </Link>
                                )
                              )}
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="flex flex-1 w-full flex-row items-center justify-between mb-2 mt-2">
                              <Text size="h2">{submenuSection?.name}</Text>
                              <Button
                                text="Explore"
                                invert
                                className="mb-0 py-[0px] px-[0px] border-none !text-blue"
                                onClick={() => {
                                  navigate("/" + submenuSection?.slug);
                                }}
                                icon="chevron-right"
                                iconSide="right"
                                size="medium"
                              />
                            </div>
                            <Text size="paragraph" className="mb-8">
                              {submenuSection?.description}
                            </Text>

                            <div className="">
                              <div className="mb-9">
                                <Text size={13} color="black">
                                  Top Golf Destinations
                                </Text>

                                <div className="grid grid-cols-12 grid-rows-2 gap-y-1 gap-x-1 overflow-hidden mt-5">
                                  {submenuSection?.regions
                                    .filter((r) => {
                                      return submenuSection?.order.includes(
                                        r.id
                                      );
                                    })
                                    .sort(
                                      (a, b) =>
                                        sortByOrder(submenuSection?.order)[
                                          a.id
                                        ] -
                                        sortByOrder(submenuSection?.order)[b.id]
                                    )
                                    .slice(0, 3)
                                    .map(
                                      (
                                        region: {
                                          slug: any;
                                          name: {
                                            en:
                                              | string
                                              | number
                                              | boolean
                                              | React.ReactElement<
                                                  any,
                                                  | string
                                                  | React.JSXElementConstructor<any>
                                                >
                                              | React.ReactFragment
                                              | React.ReactPortal
                                              | null
                                              | undefined;
                                          };
                                          courses: string | any[];
                                        },
                                        rIdx: React.Key | null | undefined
                                      ) => (
                                        <Link
                                          key={rIdx}
                                          to={`/${submenuSection?.slug}/${region.slug}-golf-courses`}
                                          className={`${
                                            rIdx === 0
                                              ? `aspect-h-3 aspect-w-2 row-span-2 col-span-6`
                                              : `aspect-h-1 aspect-w-1 row-span-1 col-span-6`
                                          } bg-black/80 rounded-[4px] `}
                                          style={{
                                            backgroundSize: "cover",
                                            backgroundImage: `linear-gradient(to bottom, rgba(0, 0, 0, 0) 50%, rgba(0, 0, 0, 0.5) 100%), url(https://images.unsplash.com/photo-1538648759472-7251f7cb2c2f?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1548&q=80)`,
                                          }}
                                        >
                                          <div className="justify-end flex flex-col items-start p-2">
                                            <Text
                                              size={16}
                                              weight="semibold"
                                              color="white"
                                            >
                                              {region.name.en}
                                            </Text>

                                            <Text size={12} color="white90">
                                              {region.courses.length} golf
                                              courses
                                            </Text>
                                          </div>
                                        </Link>
                                      )
                                    )}
                                </div>
                              </div>
                              <div>
                                <Text size={13} color="black">
                                  More Destinations
                                </Text>
                                <div className="grid grid-cols-2 gap-6 mt-5">
                                  {submenuSection?.regions
                                    .filter((r) => {
                                      return (
                                        !submenuSection?.order.includes(r.id) &&
                                        r?.courses?.length > 0
                                      );
                                    })
                                    .sort((a, b) => {
                                      return a.name.en.localeCompare(b.name.en);
                                    })
                                    .map(
                                      (
                                        region: {
                                          slug: any;
                                          name: {
                                            en:
                                              | string
                                              | number
                                              | boolean
                                              | React.ReactFragment
                                              | React.ReactPortal
                                              | React.ReactElement<
                                                  any,
                                                  | string
                                                  | React.JSXElementConstructor<any>
                                                >
                                              | null
                                              | undefined;
                                          };
                                          courses: string | any[];
                                        },
                                        rIdx: React.Key | null | undefined
                                      ) => (
                                        <Link
                                          key={rIdx}
                                          to={`/${submenuSection?.slug}/${region.slug}-golf-courses`}
                                        >
                                          <Text size={13} color="black80">
                                            {region.name.en}
                                          </Text>
                                          <Text size="small" color="black60">
                                            {region.courses.length} golf courses
                                          </Text>
                                        </Link>
                                      )
                                    )}
                                </div>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    </Dialog.Panel>
                  </Transition.Child>
                </div>
              </Dialog>
            </Transition.Root>
          </Dialog>
        </Transition.Root>

        <nav
          aria-label="Top"
          className="lg:max-w-[1000px] xl:max-w-[1120px] mx-auto px-[20px] md:px-[40px] lg:px-0  py-[12px] lg:py-[16px]  flex justify-between items-center"
        >
          {/* Navbar Logo */}
          <div className="flex  flex-1 ">
            <div className="flex-shrink-0  sm:w-auto sm:mr-4 flex items-center">
              <Link to="/">
                <img
                  alt={process.env.GATSBY_SITE_URL}
                  src={process.env.GATSBY_LOGO}
                  className="h-[31px] sm:h-[38px]  ml-[-2px]"
                />
              </Link>
            </div>
            {!hideNav && (
              <>
                {regionalMode ? (
                  <>
                    <div className="hidden relative z-40 p-[10px] rounded lg:flex items-center justify-center transition-colors ease-out duration-200 h-full space-x-8">
                      <Link
                        to={`/${region.node.slug}-golf-courses`}
                        className=" hover:underline"
                      >
                        <Text
                          size={15}
                          weight="semibold"
                          leading={1.3}
                          className="mb-0"
                          color={"black"}
                        >
                          {region.node.name.en} Golf Courses
                        </Text>
                      </Link>
                      <Link
                        to={`/${region.node.slug}-golf-packages`}
                        className=" hover:underline"
                      >
                        <Text
                          size={15}
                          weight="semibold"
                          leading={1.3}
                          className="mb-0"
                          color={"black"}
                        >
                          {region.node.name.en} Golf Packages
                        </Text>
                      </Link>
                    </div>
                  </>
                ) : (
                  <>
                    {searchMode ? (
                      <></>
                    ) : (
                      <Popover.Group className="hidden lg:flex-1 lg:block lg:self-stretch mt-0.5">
                        <div className="relative h-full w-full  flex space-x-4">
                          {getNavigationData()
                            .categories.sort(
                              (a: any, b: any) =>
                                sortByObject[a.slug] - sortByObject[b.slug]
                            )
                            .map((category: any, catIdx: any) => (
                              <Popover key={catIdx}>
                                {({ open }) => (
                                  <>
                                    <div
                                      className="relative flex"
                                      onMouseEnter={() => {
                                        setActiveMenu(category.id);
                                      }}
                                      onMouseLeave={(e) => {
                                        if (closestEdge(e) !== "bottom") {
                                          setActiveMenu("");
                                        }
                                      }}
                                    >
                                      <Popover.Button
                                        className={classNames(
                                          catIdx > 3 && "hidden xl:block",
                                          open ||
                                            activeMenu.includes(category.id)
                                            ? "bg-blue"
                                            : "",
                                          "relative z-40 p-[10px] rounded flex items-center justify-center transition-colors ease-out duration-200 focus:outline-none focus:ring-0"
                                        )}
                                      >
                                        <Text
                                          size={15}
                                          weight="semibold"
                                          className="mb-0"
                                          color={
                                            open ||
                                            activeMenu.includes(category.id)
                                              ? "white"
                                              : "black"
                                          }
                                        >
                                          {category.name}
                                        </Text>
                                      </Popover.Button>
                                    </div>

                                    <Transition
                                      as={Fragment}
                                      enter="transition ease-out duration-200"
                                      enterFrom="opacity-0"
                                      enterTo="opacity-100"
                                      leave="transition ease-in duration-150"
                                      leaveFrom="opacity-100"
                                      leaveTo="opacity-0"
                                      show={activeMenu.includes(category.id)}
                                    >
                                      <Popover.Panel
                                        onMouseLeave={(e) => {
                                          setActiveMenu("");
                                        }}
                                        className="max-w-[1120px] transform absolute z-40 top-full inset-x-0 mt-[5px]"
                                      >
                                        {/* Presentational element used to render the bottom shadow, if we put the shadow on the actual panel it pokes out the top, so we use this shorter element to hide the top of the shadow */}
                                        <div
                                          className="absolute inset-0 top-1/2"
                                          aria-hidden="true"
                                        />

                                        <div
                                          className="rounded-md bg-white relative"
                                          style={{
                                            boxShadow:
                                              "0px 5px 14px rgba(0,0,0,0.3)",
                                          }}
                                        >
                                          <div className="max-w-screen-xl   px-[40px] py-[32px]">
                                            <div className="flex flex-row items-center justify-between mb-2">
                                              <Text size="h2">
                                                {category.name}
                                              </Text>
                                              <Button
                                                text="Explore"
                                                className="mb-0 py-[6px] px-[10px]"
                                                onClick={() => {
                                                  navigate("/" + category.slug);
                                                }}
                                                icon="chevron-right"
                                                iconSide="right"
                                                size="medium"
                                              />
                                            </div>
                                            <Text
                                              size="paragraph"
                                              className="mb-0"
                                            >
                                              {category.description}
                                            </Text>

                                            <div className="grid grid-cols-12 mt-6 gap-10">
                                              <div className="col-span-6">
                                                <Text size={13} color="black">
                                                  Top Golf Destinations
                                                </Text>

                                                <div className="grid grid-cols-2 gap-3 mt-5">
                                                  {category.regions
                                                    .filter((r) => {
                                                      return category.order.includes(
                                                        r.id
                                                      );
                                                    })
                                                    .sort(
                                                      (a: any, b: any) =>
                                                        sortByOrder(
                                                          category.order
                                                        )[a.id] -
                                                        sortByOrder(
                                                          category.order
                                                        )[b.id]
                                                    )
                                                    .slice(0, 3)
                                                    .map(
                                                      (
                                                        region: {
                                                          slug: any;
                                                          name: any;
                                                          courses:
                                                            | string
                                                            | any[];
                                                        },
                                                        rIdx: number
                                                      ) => (
                                                        <Link
                                                          key={rIdx}
                                                          to={`/${category?.slug}/${region.slug}-golf-courses`}
                                                          className={`${
                                                            rIdx === 0
                                                              ? `aspect-h-5 aspect-w-4 row-span-2 col-span-1`
                                                              : `aspect-h-3 aspect-w-5 row-span-1 col-span-1`
                                                          } bg-black/80 rounded-[4px] `}
                                                          style={{
                                                            backgroundSize:
                                                              "cover",
                                                            backgroundImage: `linear-gradient(to bottom, rgba(0, 0, 0, 0) 50%, rgba(0, 0, 0, 0.5) 100%), url(https://images.unsplash.com/photo-1538648759472-7251f7cb2c2f?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1548&q=80)`,
                                                          }}
                                                        >
                                                          <div className="justify-end flex flex-col items-start p-2">
                                                            <Text
                                                              size={15}
                                                              weight="semibold"
                                                              color="white"
                                                            >
                                                              {region.name.en}
                                                            </Text>

                                                            <Text
                                                              size={13}
                                                              color="white90"
                                                            >
                                                              {
                                                                region.courses
                                                                  .length
                                                              }{" "}
                                                              golf courses
                                                            </Text>
                                                          </div>
                                                        </Link>
                                                      )
                                                    )}
                                                </div>
                                              </div>
                                              <div className="col-span-6">
                                                <Text size={13} color="black">
                                                  More Destinations
                                                </Text>
                                                <div className="grid grid-cols-2 gap-6 mt-5">
                                                  {category.regions
                                                    .filter((r: any) => {
                                                      return (
                                                        !category.order.includes(
                                                          r.id
                                                        ) &&
                                                        r?.courses?.length > 0
                                                      );
                                                    })
                                                    .sort((a: any, b: any) => {
                                                      return a.name.en.localeCompare(
                                                        b.name.en
                                                      );
                                                    })
                                                    .map((region, rIdx) => (
                                                      <Link
                                                        key={rIdx}
                                                        to={`/${category.slug}/${region.slug}-golf-courses`}
                                                      >
                                                        <Text
                                                          size={13}
                                                          color="black80"
                                                        >
                                                          {region.name.en}
                                                        </Text>
                                                        <Text
                                                          size="small"
                                                          color="black60"
                                                        >
                                                          {
                                                            region.courses
                                                              .length
                                                          }{" "}
                                                          golf courses
                                                        </Text>
                                                      </Link>
                                                    ))}
                                                </div>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      </Popover.Panel>
                                    </Transition>
                                  </>
                                )}
                              </Popover>
                            ))}

                          <Popover key={"alldestinations"}>
                            {({ open }) => (
                              <>
                                <div className="relative flex">
                                  <Popover.Button
                                    className={classNames(
                                      open ||
                                        activeMenu.includes("alldestinations")
                                        ? "bg-blue"
                                        : "",
                                      "relative z-40 p-[10px] rounded flex items-center justify-center transition-colors ease-out duration-200 focus:outline-none focus:ring-0"
                                    )}
                                    onMouseEnter={() => {
                                      setActiveMenu("alldestinations");
                                    }}
                                    onMouseLeave={(e) => {
                                      if (closestEdge(e) !== "bottom") {
                                        setActiveMenu("");
                                      }
                                    }}
                                  >
                                    <Text
                                      size={15}
                                      weight="semibold"
                                      className="mb-0"
                                      color={
                                        open ||
                                        activeMenu.includes("alldestinations")
                                          ? "white"
                                          : "black"
                                      }
                                    >
                                      All Destinations
                                    </Text>
                                  </Popover.Button>
                                </div>

                                <Transition
                                  as={Fragment}
                                  enter="transition ease-out duration-200"
                                  enterFrom="opacity-0"
                                  enterTo="opacity-100"
                                  leave="transition ease-in duration-150"
                                  leaveFrom="opacity-100"
                                  leaveTo="opacity-0"
                                  show={activeMenu.includes("alldestinations")}
                                >
                                  <Popover.Panel
                                    onMouseLeave={(e) => {
                                      setActiveMenu("");
                                    }}
                                    className="max-w-[1120px]  transform absolute z-40 top-full inset-x-0 mt-[5px]"
                                  >
                                    {/* Presentational element used to render the bottom shadow, if we put the shadow on the actual panel it pokes out the top, so we use this shorter element to hide the top of the shadow */}
                                    <div
                                      className="absolute inset-0 top-1/2"
                                      aria-hidden="true"
                                    />

                                    <div
                                      className="rounded-md bg-white relative"
                                      style={{
                                        boxShadow:
                                          "0px 5px 14px rgba(0,0,0,0.3)",
                                      }}
                                    >
                                      <div className="max-w-screen-xl w-4/5  px-[40px] py-[32px]">
                                        <div className="flex flex-row items-center justify-between mb-2">
                                          <Text size="h2">
                                            All Destinations
                                          </Text>
                                        </div>
                                        <div className="grid grid-cols-4 gap-6">
                                          {getNavigationData().allDestinations.map(
                                            (category: any, catIdx: any) => (
                                              <Link
                                                key={catIdx}
                                                to={`/${category.slug}`}
                                              >
                                                <Text size={13} color="black80">
                                                  {category.name}
                                                </Text>
                                                <Text
                                                  size="small"
                                                  color="black60"
                                                >
                                                  {category.courses} golf
                                                  courses
                                                </Text>
                                              </Link>
                                            )
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </Popover.Panel>
                                </Transition>
                              </>
                            )}
                          </Popover>
                        </div>
                      </Popover.Group>
                    )}
                  </>
                )}
              </>
            )}
          </div>
          {/* Navbar Icons Icons */}
          <div className="flex items-center gap-2 sm:gap-4">
            <div className="hidden sm:block">
              <div
                onClick={() => {
                  setShowCurrencyPicker();
                }}
                className={`cursor-pointer relativerounded-[4px] p-1 text-center flex justify-center items-center h-8 w-8 sm:h-10 sm:w-10  text-15 font-semibold `}
              >
                <span
                  className={`
                      hover:underline
                  }`}
                >
                  {display_currency.code}
                </span>
              </div>
            </div>
            {checkoutButton}
            {contactButton}
            {loginButton}
            <div className="py-2 pl-1 lg:hidden" onClick={() => setOpen(true)}>
              <span className="sr-only">Open menu</span>
              <MenuIcon className="h-6 w-6 text-black/80" aria-hidden="true" />
            </div>
          </div>
        </nav>
      </header>
    </>
  );
}, navPropsAreEqual);

function navPropsAreEqual(prevProps: any, nextProps: any) {
  return prevProps === nextProps;
}
export default Navbar;
